// Mirrors server/src/lib/dates.js. Dates are plain 'YYYY-MM-DD' strings everywhere; the
// moment one becomes a JS Date, a crew starts seeing tomorrow's board tonight.

/** The shape check, for anything that arrives from a URL or storage rather than the API. */
export const isIsoDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)

export function addDays(iso, days) {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d, 12) + days * 86400000)
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(
    dt.getUTCDate(),
  ).padStart(2, '0')}`
}

export function isoWeekday(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  const wd = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()
  return wd === 0 ? 7 : wd
}

export function startOfWeek(iso, weekStart = 1) {
  return addDays(iso, -((isoWeekday(iso) - weekStart + 7) % 7))
}

export function workingWeek(iso, weekStart = 1, workDays = [1, 2, 3, 4, 5]) {
  const start = startOfWeek(iso, weekStart)
  const set = new Set(workDays.map(Number))
  const out = []
  for (let i = 0; i < 7; i++) {
    const day = addDays(start, i)
    if (set.has(isoWeekday(day))) out.push(day)
  }
  return out
}

/**
 * This working week and the next, as one run of dates.
 *
 * Built from two workingWeek() calls rather than by walking fourteen days, so the tenant's
 * own week start and non-working days are applied exactly the same way twice — a fortnight
 * that skipped weekends differently from the week view would be a subtle, permanent
 * disagreement between two views of the same board.
 */
export function workingFortnight(iso, weekStart = 1, workDays = [1, 2, 3, 4, 5]) {
  return [...workingWeek(iso, weekStart, workDays), ...workingWeek(addDays(iso, 7), weekStart, workDays)]
}

/**
 * The seven days of the week containing `iso`, as a { from, to } pair — the "This week"
 * preset every period dialog offers. Three dialogs computed it separately; the tenant's week
 * start is the one thing they each had to remember to pass, and this is where it is applied.
 */
export function weekOf(iso, weekStart = 1) {
  const from = startOfWeek(iso, weekStart)
  return { from, to: addDays(from, 6) }
}

const WEEKDAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const weekdayName = (iso) => WEEKDAYS[isoWeekday(iso)]

export function formatDate(iso, { withYear = false } = {}) {
  const [y, m, d] = iso.split('-').map(Number)
  return `${weekdayName(iso)} ${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`
}

export function formatShort(iso) {
  const [, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]}`
}

export function relativeDay(iso, today) {
  if (iso === today) return 'Today'
  if (iso === addDays(today, 1)) return 'Tomorrow'
  if (iso === addDays(today, -1)) return 'Yesterday'
  return null
}

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** 'YYYY-MM' → 'August 2026'. */
export function formatMonth(ym) {
  const [y, m] = ym.split('-').map(Number)
  return `${MONTHS_LONG[m - 1]} ${y}`
}

export const monthOf = (iso) => iso.slice(0, 7)
export const firstOfMonth = (ym) => `${ym}-01`

/** Month arithmetic on 'YYYY-MM', which is the only thing a month picker ever needs. */
export function addMonths(ym, n) {
  const [y, m] = ym.split('-').map(Number)
  const total = y * 12 + (m - 1) + n
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

export function lastOfMonth(ym) {
  const next = firstOfMonth(addMonths(ym, 1))
  return addDays(next, -1)
}

/**
 * How many days a range covers, both ends included.
 *
 * Both strings parse as UTC midnight, so the difference is exact whatever the viewer's zone
 * and whatever DST does in between — the one arithmetic on dates where going through Date is
 * safe, because only the gap is used and never the instant it lands on.
 */
export function daysInRange(from, to) {
  if (!from || !to) return 0
  return Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1
}

/**
 * Every date drawn by a month grid: whole weeks, so the calendar is always a clean
 * rectangle, with the leading and trailing days belonging to the neighbouring months.
 */
export function monthGrid(ym, weekStart = 1) {
  const end = lastOfMonth(ym)
  const out = []
  let cursor = startOfWeek(firstOfMonth(ym), weekStart)
  // Run to the end of the month, then keep going until the last week is complete, so the
  // grid is always a clean rectangle rather than ending mid-row.
  while (cursor <= end || out.length % 7 !== 0) {
    out.push(cursor)
    cursor = addDays(cursor, 1)
    if (out.length >= 42) break // six weeks is the most any month can span
  }
  return out
}

/* There is no Date boundary here any more.

   There used to be: isoToDate and dateToIso, the one sanctioned place a 'YYYY-MM-DD' string
   became a JS Date and came back, because the calendar library spoke Date. Both used the
   LOCAL calendar fields on purpose — `new Date('2026-08-24')` parses as UTC midnight, which
   is still the 23rd anywhere west of Greenwich — and getting that wrong moves a crew's day by
   one, for some users, in some timezones, on some days.

   The calendar is now drawn from monthGrid() below, so nothing converts and there is no
   correct-but-fragile pair of functions left to get wrong. Do not reintroduce them. */

/* ── Times of day ────────────────────────────────────────────────────────
   Time off can cover part of a day. A time is an 'HH:MM' string, 24-hour, the whole way
   through — the same discipline as dates and for the same reason: the moment one becomes a
   Date it acquires a zone, and a dentist at 8am in Vancouver is not at 8am anywhere else.
   Nothing below constructs a Date. */

/** '08:00' → '8am', '14:30' → '2:30pm'. Compact, the way a crew says it and a wall can show it. */
export function formatTime(hhmm) {
  if (!hhmm) return ''
  const [h, m] = hhmm.split(':').map(Number)
  const suffix = h < 12 ? 'am' : 'pm'
  const hour = h % 12 === 0 ? 12 : h % 12
  return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`
}

/**
 * The two ends of a row's absence — '8am–2pm' — or null when it is the whole day.
 *
 * For a period this is the START time on the first day and the END time on the last, so it
 * reads correctly beside the dates it belongs to. Where only one date is in view, use
 * windowLabel instead: on the middle of a period there are no hours to show.
 */
export function timeWindow(t) {
  return t?.startTime && t?.endTime ? `${formatTime(t.startTime)}–${formatTime(t.endTime)}` : null
}

/** True when the row carries a time window at all, rather than covering whole days. */
export const hasHours = (t) => Boolean(t?.startTime && t?.endTime)

/**
 * What a row covers on one particular date: null, 'all', or the window within that day.
 *
 * A part-day absence is ONE CONTINUOUS STRETCH — away from the start time on the first day,
 * through every day between, until the end time on the last. Not the same hours repeated
 * daily, which is what it used to mean: "off from 2pm Thursday until 9am the following
 * Tuesday" is how people describe leave, and reading it as "2pm to 9am each day" described a
 * completely different absence while looking identical on screen.
 *
 * So the first and last days are partly free and the ones between are not, which is why every
 * caller has to ask about a DATE rather than about the row.
 */
export function coverageOn(t, date) {
  if (!t || !date || date < t.from || date > t.to) return null
  if (!hasHours(t)) return 'all'
  const first = date === t.from
  const last = date === t.to
  if (first && last) return { start: t.startTime, end: t.endTime }
  if (first) return { start: t.startTime, end: null } // away from then until the day ends
  if (last) return { start: null, end: t.endTime } // away until then
  return 'all'
}

/** True when this date is only partly covered, so there is still time on it to be booked. */
export function isPartialOn(t, date) {
  const c = coverageOn(t, date)
  // `typeof null` is 'object', so the null has to be ruled out first or a date the row does
  // not cover at all would read as partly covered.
  return c !== null && c !== 'all'
}

/** How the hours read on one date: '9am–1pm', 'from 9am', 'until 1pm', or null. */
export function windowLabel(t, date) {
  const c = coverageOn(t, date)
  if (!c || c === 'all') return null
  if (c.start && c.end) return `${formatTime(c.start)}–${formatTime(c.end)}`
  return c.start ? `from ${formatTime(c.start)}` : `until ${formatTime(c.end)}`
}

/**
 * Why a part-day window as typed cannot be saved, or null when it can.
 *
 * Mirrors readWindow in server/src/repos/timeoff.js: both ends or neither, and the end after
 * the start ONLY when the two fall on the same day. Across a period the window is one
 * continuous absence — "off from 2pm Thursday until 9am Tuesday" — so the end is allowed to be
 * earlier in the day than the start. Two dialogs used to refuse exactly that booking while the
 * hint beside the fields said it was allowed, and the server would have taken it.
 *
 * `partial` false is always valid: the times are ignored and the whole day is meant.
 */
export function hoursWindowError({ partial, startTime, endTime }, from, to) {
  if (!partial) return null
  if (!startTime || !endTime) return 'Give both a start and an end time.'
  if (from === to && endTime <= startTime) return 'The end time is before the start time.'
  return null
}
